import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchenDining } from "../../../redux/actions/kitchenDiningActions";
import { toast } from "react-hot-toast";
import CameraCapture from "../../../components/shared/CameraCapture";
import { useLanguage } from "../../../context/LanguageContext";

import {
  ArrowLeft,
  Save,
  AlertCircle,
  CheckCircle,
  Upload,
  Camera,
  FileText,
  User,
  Shield,
  Hand,
  Stethoscope,
  Thermometer,
  AlertTriangle,
  Calendar,
  Heart,
  Package,
  ClipboardCheck,
} from "lucide-react";

const SectionHPersonalHygiene = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraField, setCameraField] = useState(null);
  const [previewImages, setPreviewImages] = useState({
    h1Photo: null,
    h3Document: null,
  });

  const [form, setForm] = useState({
    h1: "",
    h1Photo: null,
    h2: "",
    h3: "",
    h3Date: "",
    h3Document: null,
    h4: "",
    remarks: "",
  });


  const [errors, setErrors] = useState({});

  // Load saved draft if exists
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const savedData = localStorage.getItem("survey_section_h");
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          const { timestamp, canteenInfo, ...formData } = parsedData;
          setForm(formData);
        }
      } catch (err) {
        console.error("Error loading saved data:", err);
      }
    };

    loadSavedData();
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(form).some((v) => v !== "" && v !== null)) {
        localStorage.setItem("survey_section_h", JSON.stringify(form));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [form]);

  useEffect(() => {
  return () => {
    Object.values(previewImages).forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
  };
}, [previewImages]);
  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];

      // Validate file size immediately
      if (file && file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_size,
        }));
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (file && !allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_type_long,
        }));
        return;
      }

      setForm((prev) => ({ ...prev, [name]: file }));

      if (file && file.type.startsWith("image/")) {
        const previewUrl = URL.createObjectURL(file);
        setPreviewImages((prev) => ({
          ...prev,
          [name]: previewUrl,
        }));
      }
      // Clear file error if validation passes
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user makes a selection
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations for all radio buttons
    if (!form.h1) newErrors.h1 = t.global.err_a1;
    if (!form.h2) newErrors.h2 = t.global.err_a1;
    if (!form.h3) newErrors.h3 = t.global.err_a1;
    if (!form.h4) newErrors.h4 = t.global.err_a1;

    // File validation only if file IS uploaded (optional but recommended)
    if (form.h1Photo && form.h1Photo.size > 5 * 1024 * 1024) {
      newErrors.h1Photo = t.global.err_file_size;
    }
    if (form.h1Photo) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.h1Photo.type)) {
        newErrors.h1Photo = t.global.err_file_type_long;
      }
    }

    if (form.h3Document && form.h3Document.size > 5 * 1024 * 1024) {
      newErrors.h3Document = t.global.err_file_size;
    }
    if (form.h3Document) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.h3Document.type)) {
        newErrors.h3Document = t.global.err_file_type_long;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Get latitude & longitude automatically
      let latitude = "";
      let longitude = "";

      if (navigator.geolocation) {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              latitude = position.coords.latitude;
              longitude = position.coords.longitude;
              resolve();
            },
            (error) => {
              console.warn("Geolocation error:", error);
              resolve(); // still proceed without location
            },
            { timeout: 10000, enableHighAccuracy: true },
          );
        });
      }

      // Create FormData for API
      const fd = new FormData();
      fd.append("reporting_date", new Date().toISOString().split("T")[0]);
      fd.append("district_id", state?.district_id);
      fd.append("municipality_id", state?.municipality_id);
      fd.append("ward_id", state?.ward_id);
      fd.append("area_id", state?.area_id);
      fd.append("canteen_type_id", state?.canteen_type_id);
      fd.append("latitude", latitude || "0");
      fd.append("longitude", longitude || "0");

      // Section H data mapping
      const yesNoMap = { [t.global.yes]: "1", [t.global.no]: "2" };

      fd.append("pers_hygn_H1", yesNoMap[form.h1]);

      if (form.h1 === t.global.yes && form.h1Photo) {
        fd.append("pers_hygn_H1_upload", form.h1Photo);
      }

      fd.append("pers_hygn_H2", yesNoMap[form.h2]);
      fd.append("pers_hygn_H3", yesNoMap[form.h3]);

      if (form.h3 === t.global.yes) {
        if (form.h3Date) {
          fd.append("pers_hygn_H3_date", form.h3Date);
        } else {
          // Use current date as fallback
          fd.append(
            "pers_hygn_H3_date",
            new Date().toISOString().split("T")[0],
          );
        }

        if (form.h3Document) {
          fd.append("pers_hygn_H3_upload", form.h3Document);
        }
      }

      fd.append("pers_hygn_H4", yesNoMap[form.h4]);

      if (form.remarks) {
        fd.append("hygiene_remarks", form.remarks);
      }

      fd.append("current_section", "personalhygiene");

      // Dispatch the action
      await dispatch(createKitchenDining(fd));

      // ✅ Check if this section is partial (missing optional uploads/date when YES is selected)
      const isPartial =
        (form.h1 === t.global.yes && !form.h1Photo) ||
        (form.h3 === t.global.yes && (!form.h3Date || !form.h3Document));

      // ✅ Save to localStorage with partial flag
      const sectionData = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
      };

      localStorage.setItem("survey_section_h", JSON.stringify(sectionData));

      // ✅ Mark Section H as submitted (could be partial)
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.h = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      // Show success message
      setShowSuccess(true);

      // Auto-navigate after 2 seconds
      setTimeout(() => {
        setShowSuccess(false);
        navigate("/survey/sections", { state });
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);

      // Save locally for offline
      const isPartial =
        (form.h1 === t.global.yes && !form.h1Photo) ||
        (form.h3 === t.global.yes && (!form.h3Date || !form.h3Document));

      const sectionData = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
        savedLocally: true,
      };

      localStorage.setItem("survey_section_h", JSON.stringify(sectionData));

      // Mark as completed locally
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.h = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      toast.error(t.global.alert_saved_locally);
      setIsSubmitting(false);
      navigate("/survey/sections", { state });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Message Overlay */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full text-center animate-scale-in">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.global.success_title}
              </h3>
              <p className="text-gray-600 mb-6">
                Section H submitted successfully.
              </p>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                  {t.global.redirecting}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  navigate("/survey/sections", { state });
                }}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {t.global.continue_now}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kitchenCumDining.sections.H.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kitchenCumDining.sections.H.subtitle}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full flex items-center">
                <User size={14} className="mr-1" />
                Personal Hygiene Survey
              </div>
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.area_id}: ${state.area_name}` : t.global.na}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* H1 - Protective Gear */}
            <div className="border rounded-lg p-4 bg-purple-50 border-purple-200">
              <label className="block font-medium mb-3 flex items-center">
                <Shield size={18} className="mr-2" />
                H.1 {t.modules.kitchenCumDining.sections.H.questions.protectiveGear}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-800 font-medium mb-2">
                  {t.modules.kitchenCumDining.sections.H.hints.protectiveGear.split(":")[0]}:
                </p>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <div className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded mr-2">
                      i
                    </div>
                    <span className="text-sm text-gray-700">
                      Apron and cap while cutting vegetables & washing utensils
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded mr-2">
                      ii
                    </div>
                    <span className="text-sm text-gray-700">
                      Apron, cap and mask while cooking
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded mr-2">
                      iii
                    </div>
                    <span className="text-sm text-gray-700">
                      Apron, cap, mask and food handling gloves during serving food
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded mr-2">
                      iv
                    </div>
                    <span className="text-sm text-gray-700">
                      Apron and utility gloves while cleaning and mopping kitchen and its surroundings
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.h1 === option
                        ? "bg-purple-100 border-purple-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.h1 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="h1"
                      value={option}
                      checked={form.h1 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.h1 === option
                            ? "text-purple-700"
                            : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.h1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.h1}
                </p>
              )}

              {form.h1 === t.global.yes && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-3 text-gray-700">
                    Upload picture of staff wearing PPE (Optional):
                    <span className="text-gray-500 ml-1">
                      Recommended but not required
                    </span>
                  </label>

                  {/* CAMERA + UPLOAD BUTTONS */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("h1Photo");
                        setShowCamera(true);
                      }}
                      disabled={isSubmitting}
                      className="flex items-center justify-center gap-2 p-3 border border-purple-300 bg-purple-900/10 text-purple-900 rounded-lg active:scale-95 active:bg-purple-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                    >
                      <Camera size={18} /> {t.global.use_camera}
                    </button>

                    <label
                      htmlFor="h1Photo"
                      className={`flex items-center justify-center gap-2 p-3 border border-purple-300 bg-purple-900/10 text-purple-900 rounded-lg active:scale-95 active:bg-purple-700 active:text-white transition-all duration-150 cursor-pointer font-medium ${
                        isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <Upload size={18} /> {t.global.upload_file}
                    </label>
                  </div>

                  {/* HIDDEN INPUT */}
                  <input
                    type="file"
                    name="h1Photo"
                    id="h1Photo"
                    accept="image/*"
                    onChange={handleChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />

                  {/* PREVIEW BOX */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.h1Photo
                        ? "border-purple-400 bg-purple-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
{form.h1Photo ? (
  <div className="space-y-4">
    {/* IMAGE PREVIEW */}
    {previewImages.h1Photo &&
      form.h1Photo.type.startsWith("image/") && (
        <img
          src={previewImages.h1Photo}
          alt="PPE Preview"
          className="mx-auto max-h-60 rounded-lg border shadow-sm"
        />
      )}

    {/* FILE INFO */}
    <div className="flex items-center justify-between">
      <div className="text-left">
        <p className="text-gray-700 font-medium text-sm break-all">
          {form.h1Photo.name}
        </p>
        <p className="text-xs text-gray-600 mt-1">
          Type: {form.h1Photo.name?.split(".").pop()?.toUpperCase()}
        </p>
        <p className="text-xs text-gray-600">
          Size: {(form.h1Photo.size / 1024).toFixed(1)} KB
        </p>
      </div>

      <button
        type="button"
        onClick={() => {
          setForm((prev) => ({ ...prev, h1Photo: null }));
          setPreviewImages((prev) => ({ ...prev, h1Photo: null }));
        }}
        className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
      >
        {t.global.remove}
      </button>
    </div>
  </div>
) : (
  <>
    <p className="text-gray-400 italic text-sm">
      Capture or upload PPE photo
    </p>
    <p className="text-xs text-amber-600 mt-2">
      Missing optional photo - Section may be marked as partial
    </p>
  </>
)}
                  </div>

                  {/* STATUS */}
                  {form.h1Photo && (
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-sm text-green-600 flex items-center">
                        <CheckCircle size={14} className="mr-1" />
                        Photo selected (will be fully completed)
                      </p>
                    </div>
                  )}

                  {errors.h1Photo && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      {errors.h1Photo}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* H2 - Handwashing */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Hand size={18} className="mr-2" />
                H.2 {t.modules.kitchenCumDining.sections.H.questions.handwashingTraining}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-800 font-medium mb-2">
                  {t.modules.kitchenCumDining.sections.H.hints.handwashingTraining.split(":")[0]} (Observe staff):
                </p>
                <ol className="text-sm text-gray-700 list-decimal pl-5 space-y-1">
                  <li>Wet hands with clean running water</li>
                  <li>Apply enough soap to cover all hand surfaces</li>
                  <li>Rub hands palm to palm</li>
                  <li>
                    Right palm over left dorsum with interlaced fingers and vice versa
                  </li>
                  <li>Palm to palm with fingers interlaced</li>
                  <li>
                    Rotational rubbing of left thumb clasped in right palm and vice versa
                  </li>
                  <li>
                    Rotational rubbing, backwards and forwards with clasped fingers of right hand in left palm and vice versa
                  </li>
                  <li>Rinse hands with water</li>
                  <li>Dry hands thoroughly with single-use towel</li>
                </ol>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.h2 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.h2 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="h2"
                      value={option}
                      checked={form.h2 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.h2 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.h2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.h2}
                </p>
              )}
            </div>

            {/* H3 - Health Check-ups */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Stethoscope size={18} className="mr-2" />
                H.3 {t.modules.kitchenCumDining.sections.H.questions.healthCheckups}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.h3 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.h3 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="h3"
                      value={option}
                      checked={form.h3 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.h3 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.h3 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.h3}
                </p>
              )}

              {form.h3 === t.global.yes && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-3 text-gray-700">
                    Upload health check-up records (Optional):
                    <span className="text-gray-500 ml-1">
                      Recommended but not required
                    </span>
                  </label>

                  {/* CAMERA + UPLOAD BUTTONS */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("h3Document");
                        setShowCamera(true);
                      }}
                      disabled={isSubmitting}
                      className="flex items-center justify-center gap-2 p-3 border border-blue-300 bg-blue-900/10 text-blue-900 rounded-lg active:scale-95 active:bg-blue-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                    >
                      <Camera size={18} /> {t.global.use_camera}
                    </button>

                    <label
                      htmlFor="h3Document"
                      className={`flex items-center justify-center gap-2 p-3 border border-blue-300 bg-blue-900/10 text-blue-900 rounded-lg active:scale-95 active:bg-blue-700 active:text-white transition-all duration-150 cursor-pointer font-medium ${
                        isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <Upload size={18} /> {t.global.upload_file}
                    </label>
                  </div>

                  {/* HIDDEN INPUT */}
                  <input
                    type="file"
                    name="h3Document"
                    id="h3Document"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />

                  {/* PREVIEW BOX */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.h3Document
                        ? "border-blue-400 bg-blue-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
{form.h3Document ? (
  <div className="space-y-4">
    {/* IMAGE PREVIEW (if image) */}
    {previewImages.h3Document &&
      form.h3Document.type.startsWith("image/") && (
        <img
          src={previewImages.h3Document}
          alt="Health Record Preview"
          className="mx-auto max-h-60 rounded-lg border shadow-sm"
        />
      )}

    {/* PDF PREVIEW ICON */}
    {form.h3Document.type === "application/pdf" && (
      <div className="flex flex-col items-center text-red-600">
        <FileText size={48} />
        <p className="text-sm mt-2">PDF Document Selected</p>
      </div>
    )}

    {/* FILE INFO */}
    <div className="flex items-center justify-between">
      <div className="text-left">
        <p className="text-gray-700 font-medium text-sm break-all">
          {form.h3Document.name}
        </p>
        <p className="text-xs text-gray-600 mt-1">
          Type: {form.h3Document.name?.split(".").pop()?.toUpperCase()}
        </p>
        <p className="text-xs text-gray-600">
          Size: {(form.h3Document.size / 1024).toFixed(1)} KB
        </p>
      </div>

      <button
        type="button"
        onClick={() => {
          setForm((prev) => ({ ...prev, h3Document: null }));
          setPreviewImages((prev) => ({ ...prev, h3Document: null }));
        }}
        className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
      >
        {t.global.remove}
      </button>
    </div>
  </div>
) : (
  <>
    <p className="text-gray-400 italic text-sm">
      Capture or upload health record
    </p>
    <p className="text-xs text-amber-600 mt-2">
      Missing optional document - Section may be marked as partial
    </p>
  </>
)}
                  </div>

                  {form.h3Document && (
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-sm text-green-600 flex items-center">
                        <CheckCircle size={14} className="mr-1" />
                        Document selected (will be fully completed)
                      </p>
                    </div>
                  )}

                  {errors.h3Document && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      {errors.h3Document}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* H4 - Personal Hygiene */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Heart size={18} className="mr-2" />
                H.4 {t.modules.kitchenCumDining.sections.H.questions.personalHygiene}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-800 font-medium mb-2">
                  {t.modules.kitchenCumDining.sections.H.hints.personalHygiene.split(":")[0]} (Observe staff):
                </p>
                <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                  <li>No nail polish or artificial nails</li>
                  <li>Nails trimmed short and clean</li>
                  <li>Hair completely covered with cap</li>
                  <li>Clean uniform/apron daily</li>
                  <li>No jewelry (except plain wedding band)</li>
                  <li>No visible illness or infections</li>
                  <li>Good personal grooming habits</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.h4 === option
                        ? option === t.global.yes
                          ? "bg-green-100 border-green-400 shadow-sm"
                          : "bg-red-100 border-red-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.h4 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="h4"
                      value={option}
                      checked={form.h4 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.h4 === option
                            ? option === t.global.yes
                              ? "text-green-700"
                              : "text-red-700"
                            : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.h4 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.h4}
                </p>
              )}
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-500 font-medium">
                  * Required fields must be completed
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  All other information is optional but recommended for comprehensive personal hygiene assessment.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center"
                disabled={isSubmitting}
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                  isSubmitting
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t.global.submitting}
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    {t.global.submit_section_h}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animation */}
      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
{showCamera && (
  <CameraCapture
    onCapture={(file) => {
      setForm((prev) => ({
        ...prev,
        [cameraField]: file,
      }));

      if (file && file.type.startsWith("image/")) {
        const previewUrl = URL.createObjectURL(file);
        setPreviewImages((prev) => ({
          ...prev,
          [cameraField]: previewUrl,
        }));
      }

      setShowCamera(false);
    }}
    onClose={() => setShowCamera(false)}
  />
)}
    </div>
  );
};

export default SectionHPersonalHygiene;